/** Home "From Precepi" strip: curated announcements from static config (see fromPrecepiItems.ts). */
import React from 'react';
import { Play } from 'lucide-react';
import { ShellNavLink } from './ShellNavLink';
import type { FromPrecepiItem } from './fromPrecepiItems';

interface Props {
  items: FromPrecepiItem[];
  allUpdatesUrl: string | null;
  /** Confirmed admin only; deliberately not the launcher's `isAdmin || loading`. */
  isAdmin: boolean;
}

const BUTTON =
  'flex h-[34px] items-center gap-2 self-start rounded-[3px] bg-[#F2F1EC] px-3 text-[13px] font-medium text-ink-deep no-underline';

const Action: React.FC<{ item: FromPrecepiItem }> = ({ item }) => {
  const a = item.action;
  if (!a) return null;
  const content = (
    <>
      {item.kind === 'training' && <Play size={12} fill="currentColor" aria-hidden="true" />}
      {a.label}
    </>
  );
  if (a.href.startsWith('/')) {
    return <ShellNavLink to={a.href} replace className={BUTTON}>{content}</ShellNavLink>;
  }
  return <a href={a.href} target="_blank" rel="noopener noreferrer" className={BUTTON}>{content}</a>;
};

export const FromPrecepi: React.FC<Props> = ({ items, allUpdatesUrl, isAdmin }) => {
  const visible = items.filter((i) => !i.adminOnly || isAdmin);
  if (visible.length === 0) return null;
  return (
    <section
      aria-label="From Precepi"
      data-testid="home-from-precepi"
      className="flex flex-col gap-4 rounded bg-ink-deep px-6 py-[22px] text-[#F2F1EC]"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-mono text-[11px] tracking-[0.14em] text-[#7FB8B0]">FROM PRECEPI</span>
        {allUpdatesUrl && (
          <a href={allUpdatesUrl} target="_blank" rel="noopener noreferrer" className="text-[13px] text-[#7FB8B0] no-underline hover:underline">
            All updates
          </a>
        )}
      </div>
      <div className="grid [grid-template-columns:repeat(auto-fit,minmax(min(240px,100%),1fr))]">
        {visible.map((item) => (
          <div
            key={item.id}
            data-testid={`from-precepi-item-${item.id}`}
            className="flex flex-col gap-2 border-[rgba(242,241,236,0.14)] py-1 pr-5 [&:not(:first-child)]:border-l [&:not(:first-child)]:pl-5"
          >
            <span className={`font-mono text-[11px] tracking-[0.1em] ${item.kind === 'new' ? 'text-[#E7A796]' : 'text-[#A8B5AE]'}`}>
              {item.eyebrow}
            </span>
            <span className="font-display text-[17px] font-medium leading-[1.3]">{item.title}</span>
            {item.body && <span className="text-[13px] leading-[1.5] text-[#C9D1CC]">{item.body}</span>}
            <Action item={item} />
            {item.kind === 'training' && (
              <ShellNavLink to="/training" replace className="self-start text-[13px] text-[#7FB8B0] no-underline hover:underline">
                All training videos
              </ShellNavLink>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default FromPrecepi;
